type ListCacheEntry = {
    items: unknown[];
    totalCount: number;
    timestamp: number;
};

export type ListCacheParams = Record<string, unknown>;

const CACHE_TTL_MS = 60_000;
const cache = new Map<string, ListCacheEntry>();

/** Treat null / undefined / "" the same so clear-search hits the unfiltered cache. */
function normalizeCacheValue(key: string, value: unknown): unknown {
    if (key === "searchItem" || key === "search") {
        if (value == null || value === "") {
            return null;
        }
        if (typeof value === "string") {
            const trimmed = value.trim();
            return trimmed === "" ? null : trimmed;
        }
    }
    return value;
}

export function normalizeListCacheParams(params: ListCacheParams = {}): ListCacheParams {
    const normalized: ListCacheParams = {};
    for (const [key, value] of Object.entries(params)) {
        normalized[key] = normalizeCacheValue(key, value);
    }
    return normalized;
}

export function buildListCacheKey(page: string, params: ListCacheParams = {}): string {
    return `${page}:${JSON.stringify(normalizeListCacheParams(params))}`;
}

export function readListCache(key: string): ListCacheEntry | null {
    const entry = cache.get(key);
    if (!entry) {
        return null;
    }
    if (Date.now() - entry.timestamp > CACHE_TTL_MS) {
        cache.delete(key);
        return null;
    }
    return entry;
}

export function writeListCache(key: string, items: unknown[], totalCount: number): void {
    cache.set(key, { items, totalCount, timestamp: Date.now() });
}

export function invalidateListCache(key: string): void {
    cache.delete(key);
}

export function prependListCacheItem(
    pageId: string,
    params: ListCacheParams,
    item: unknown
): void {
    if (!item || typeof item !== "object") {
        return;
    }

    const itemId = (item as { id?: string | number }).id;
    const cacheKey = buildListCacheKey(pageId, params);
    const cached = readListCache(cacheKey);
    const existingItems = (cached?.items ?? []) as Array<{ id?: string | number }>;
    const nextItems =
        itemId == null
            ? [item, ...existingItems]
            : [item, ...existingItems.filter((entry) => String(entry.id) !== String(itemId))];

    writeListCache(cacheKey, nextItems, nextItems.length);
}

export function invalidateListCacheByPage(pageId: string): void {
    const prefix = `${pageId}:`;
    cache.forEach((_, key) => {
        if (key.startsWith(prefix)) {
            cache.delete(key);
        }
    });
}

export type OwnershipTransferResource = "envelope" | "template" | "form";

/** Drop stale list caches after any ownership transfer so reloads fetch fresh owners. */
export function invalidateOwnershipTransferListCaches(
    resource: OwnershipTransferResource,
    options?: { formId?: string }
): void {
    if (resource === "envelope") {
        invalidateListCacheByPage("envelopes");
        cache.forEach((_, key) => {
            if (key.startsWith("form-") && key.includes("-envelopes:")) {
                cache.delete(key);
            }
        });
        if (options?.formId) {
            invalidateListCacheByPage(`form-${options.formId}-envelopes`);
        }
        return;
    }

    if (resource === "template") {
        invalidateListCacheByPage("templates");
        return;
    }

    invalidateListCacheByPage("forms");
    invalidateListCacheByPage("web-forms");
    if (options?.formId) {
        invalidateListCacheByPage(`form-${options.formId}-envelopes`);
    }
}

export function getListCacheBootstrap(pageId: string, params: ListCacheParams = {}) {
    const cacheKey = buildListCacheKey(pageId, params);
    const cached = readListCache(cacheKey);
    return {
        cacheKey,
        cached,
        hasCache: Boolean(cached),
        items: cached?.items ?? [],
        totalCount: cached?.totalCount ?? 0,
    };
}

export function isListFullyFetched(itemsLength: number, totalCount: number, pageSize: number): boolean {
    return itemsLength < pageSize || itemsLength >= totalCount;
}

/** Bottom-row skeletons are for infinite scroll only, not page-1 refetch/sort. */
export function isInfiniteScrollLoading(isLoading: boolean, isInitialLoad: boolean): boolean {
    return isLoading && !isInitialLoad;
}
